// Flat vector flags for the wine countries in ./countries.ts, drawn as SVG
// children in a 24×16 (3:2) box; the caller owns the <svg>, so size,
// viewBox and accessibility live there. Everything here is decorative and
// self-contained: no ids (one flag renders many times per screen), no text,
// gradients or filters, and nothing drawn past the box, because a
// letterboxed <svg> would show the spill. Tricolours are exact; emblems are
// reduced to a silhouette that still reads at 16×11 px.

const W = 24;
const H = 16;
const WHITE = "#fff";

// The box diagonal's trigonometry, shared by every band that runs corner to
// corner (the saltires, South Africa's arms).
const DIAGONAL = Math.hypot(W, H);
const SIN = H / DIAGONAL;
const COS = W / DIAGONAL;

/** Canton side on a nine-stripe flag (GR, UY): five stripes tall, and square. */
const FIVE_STRIPES = (5 * H) / 9;

type Point = readonly [number, number];

/** Two decimals keep DOM attributes legible with no visible error at 48 px. */
const round = (n: number): number => Math.round(n * 100) / 100;

function points(coords: readonly Point[]): string {
  return coords.map(([x, y]) => `${round(x)},${round(y)}`).join(" ");
}

function alternating(count: number, even: string, odd: string): string[] {
  return Array.from({ length: count }, (_, i) => (i % 2 === 0 ? even : odd));
}

/** Stripes along one axis, weighted when given (Spain's 1:2:1). Each rect
 * runs to the far edge so adjacent stripes never leave an anti-aliased seam. */
function stripes(
  axis: "x" | "y",
  colours: readonly string[],
  weights?: readonly number[],
): React.ReactNode {
  const weight = (i: number): number => weights?.[i] ?? 1;
  const total = colours.reduce((sum, _, i) => sum + weight(i), 0);
  const span = axis === "x" ? W : H;
  let from = 0;
  return colours.map((fill, i) => {
    const start = round(from);
    from += (span * weight(i)) / total;
    return axis === "x" ? (
      <rect key={i} x={start} width={round(W - start)} height={H} fill={fill} />
    ) : (
      <rect key={i} y={start} width={W} height={round(H - start)} fill={fill} />
    );
  });
}

/** Horizontal bands, top to bottom (the Dutch kind). */
function horizontal(colours: readonly string[], weights?: readonly number[]): React.ReactNode {
  return stripes("y", colours, weights);
}

/** Vertical bands, hoist to fly (the French kind). */
function vertical(colours: readonly string[], weights?: readonly number[]): React.ReactNode {
  return stripes("x", colours, weights);
}

/** A plus-shaped cross centred on (cx, cy), spanning w × h, bars `arm` thick. */
function cross(
  cx: number,
  cy: number,
  w: number,
  h: number,
  arm: number,
  fill: string,
): React.ReactNode {
  return (
    <>
      <rect
        x={round(cx - arm / 2)}
        y={round(cy - h / 2)}
        width={round(arm)}
        height={h}
        fill={fill}
      />
      <rect
        x={round(cx - w / 2)}
        y={round(cy - arm / 2)}
        width={w}
        height={round(arm)}
        fill={fill}
      />
    </>
  );
}

/** A corner-to-corner band of perpendicular half-width `t`, cut as a polygon
 * so it ends at the box instead of spilling past it the way a stroke would;
 * `rising` mirrors it onto the other diagonal. */
function diagonal(t: number, fill: string, rising = false): React.ReactNode {
  const a = t / SIN;
  const b = t / COS;
  const coords: readonly Point[] = [
    [a, 0],
    [W, H - b],
    [W, H],
    [W - a, H],
    [0, b],
    [0, 0],
  ];
  const mirrored = rising ? coords.map(([x, y]): Point => [W - x, y]) : coords;
  return <polygon points={points(mirrored)} fill={fill} />;
}

/** A five-point star as one polygon; `angle` is where its first point faces (up by default). */
function star(
  cx: number,
  cy: number,
  r: number,
  fill: string,
  angle = -Math.PI / 2,
): React.ReactNode {
  const coords = Array.from({ length: 10 }, (_, i): Point => {
    const radius = i % 2 === 0 ? r : r * 0.382;
    const a = angle + (i * Math.PI) / 5;
    return [cx + radius * Math.cos(a), cy + radius * Math.sin(a)];
  });
  return <polygon points={points(coords)} fill={fill} />;
}

/** Small discs: the stand-in for stars, which would vanish at 16 px. */
function dots(coords: readonly Point[], r: number, fill: string): React.ReactNode {
  return coords.map(([x, y]) => <circle key={`${x},${y}`} cx={x} cy={y} r={r} fill={fill} />);
}

/** A sun of May reduced to a disc and eight short rays. */
function sun(cx: number, cy: number, r: number, fill: string): React.ReactNode {
  const rays = Array.from({ length: 8 }, (_, i) => {
    const a = (i * Math.PI) / 4;
    const at = (k: number): string =>
      `${round(cx + Math.cos(a) * r * k)} ${round(cy + Math.sin(a) * r * k)}`;
    return `M${at(1.3)}L${at(1.9)}`;
  }).join("");
  return (
    <>
      <circle cx={cx} cy={cy} r={r} fill={fill} />
      <path d={rays} fill="none" stroke={fill} strokeWidth={round(r * 0.4)} strokeLinecap="round" />
    </>
  );
}

/** Path data for a heater shield: flat top at `top`, straight sides, round bottom. */
function shield(cx: number, top: number, w: number, h: number): string {
  const r = w / 2;
  return `M${round(cx - r)} ${round(top)}h${round(w)}v${round(h - r)}a${round(r)} ${round(r)} 0 0 1 ${round(-w)} 0z`;
}

/** South Africa's Y at perpendicular half-width `t`: arms from the hoist
 * corners to the centre, then a bar to the fly. */
function fork(t: number): string {
  const a = t / SIN;
  const x = a + ((H / 2 - t) * W) / H;
  return points([
    [a, 0],
    [x, H / 2 - t],
    [W, H / 2 - t],
    [W, H / 2 + t],
    [x, H / 2 + t],
    [a, H],
    [0, H],
    [0, 0],
  ]);
}

/** The hoist triangle enclosed by arms whose inner edges sit `t` from their centre lines. */
function hoistTriangle(t: number): string {
  const b = t / COS;
  return points([
    [0, b],
    [((H / 2 - b) * W) / H, H / 2],
    [0, H - b],
  ]);
}

/** The Union Flag without counterchange; AU and NZ scale it into their canton. */
function unionJack(): React.ReactNode {
  return (
    <>
      <rect width={W} height={H} fill="#012169" />
      {diagonal(H / 10, WHITE)}
      {diagonal(H / 10, WHITE, true)}
      {diagonal(H / 30, "#c8102e")}
      {diagonal(H / 30, "#c8102e", true)}
      {cross(W / 2, H / 2, W, H, H / 3, WHITE)}
      {cross(W / 2, H / 2, W, H, H / 5, "#c8102e")}
    </>
  );
}

/** The right half of a simplified eleven-point maple leaf, top to stem; mirrored for the left. */
const LEAF_HALF: readonly Point[] = [
  [12, 3],
  [12.5, 4.9],
  [13.6, 4.5],
  [13.4, 6.5],
  [15.3, 6.2],
  [14.5, 7.7],
  [15, 10],
  [13, 9.4],
  [12.3, 10.6],
  [12.3, 13],
];
const MAPLE_LEAF: readonly Point[] = [
  ...LEAF_HALF,
  ...LEAF_HALF.slice(1)
    .toReversed()
    .map(([x, y]): Point => [W - x, y]),
];

const SOUTHERN_CROSS_NZ: readonly Point[] = [
  [18.2, 2.9],
  [14.9, 8.4],
  [21.3, 6.2],
  [18.2, 13.1],
];

/** Centres of Georgia's four quadrants, where its small Bolnisi crosses sit. */
const BOLNISI_CROSSES: readonly Point[] = [
  [5.2, 3.2],
  [18.8, 3.2],
  [5.2, 12.8],
  [18.8, 12.8],
];

const FLAGS: Record<string, () => React.ReactNode> = {
  AR: () => (
    <>
      {horizontal(["#74acdf", WHITE, "#74acdf"])}
      {sun(W / 2, H / 2, 1.5, "#f6b40e")}
    </>
  ),
  AT: () => horizontal(["#ed2939", WHITE, "#ed2939"]),
  AU: () => (
    <>
      <rect width={W} height={H} fill="#012169" />
      <g transform="scale(0.5)">{unionJack()}</g>
      {dots([[6, 12]], 1.5, WHITE)}
      {dots(
        [
          [18, 2.7],
          [15, 8],
          [18, 13.3],
          [20.7, 6],
        ],
        1.05,
        WHITE,
      )}
      {dots([[19.5, 10]], 0.65, WHITE)}
    </>
  ),
  BE: () => vertical(["#000", "#fae042", "#ed2939"]),
  BG: () => horizontal([WHITE, "#00966e", "#d62612"]),
  BR: () => (
    <>
      <rect width={W} height={H} fill="#009c3b" />
      <polygon
        points={points([
          [2.04, 8],
          [12, 1.94],
          [21.96, 8],
          [12, 14.06],
        ])}
        fill="#ffdf00"
      />
      <circle cx={12} cy={8} r={4} fill="#002776" />
      <path d="M8.24 9.37A11 11 0 0 1 15.28 5.71" fill="none" stroke={WHITE} strokeWidth={1} />
    </>
  ),
  CA: () => (
    <>
      {vertical(["#ff0000", WHITE, "#ff0000"], [1, 2, 1])}
      <polygon points={points(MAPLE_LEAF)} fill="#ff0000" />
    </>
  ),
  CH: () => (
    <>
      <rect width={W} height={H} fill="#d52b1e" />
      {cross(W / 2, H / 2, 10, 10, 3, WHITE)}
    </>
  ),
  CL: () => (
    <>
      {horizontal([WHITE, "#d52b1e"])}
      <rect width={H / 2} height={H / 2} fill="#0039a6" />
      {star(H / 4, H / 4, 2.2, WHITE)}
    </>
  ),
  CZ: () => (
    <>
      {horizontal([WHITE, "#d7141a"])}
      <polygon
        points={points([
          [0, 0],
          [W / 2, H / 2],
          [0, H],
        ])}
        fill="#11457e"
      />
    </>
  ),
  DE: () => horizontal(["#000", "#dd0000", "#ffce00"]),
  ES: () => (
    <>
      {horizontal(["#aa151b", "#f1bf00", "#aa151b"], [1, 2, 1])}
      <rect x={5.1} y={6} width={0.8} height={4.2} fill={WHITE} />
      <rect x={10.1} y={6} width={0.8} height={4.2} fill={WHITE} />
      <rect x={6.8} y={4.6} width={2.4} height={0.8} fill="#aa151b" />
      <path d={shield(8, 5.6, 3.4, 5)} fill="#aa151b" />
    </>
  ),
  FR: () => vertical(["#0055a4", WHITE, "#ef4135"]),
  GB: unionJack,
  GE: () => (
    <>
      <rect width={W} height={H} fill={WHITE} />
      {cross(W / 2, H / 2, W, H, H / 5, "#ff0000")}
      {BOLNISI_CROSSES.map(([x, y]) => (
        <g key={`${x},${y}`}>{cross(x, y, 2.8, 2.8, 0.9, "#ff0000")}</g>
      ))}
    </>
  ),
  GR: () => (
    <>
      {horizontal(alternating(9, "#0d5eaf", WHITE))}
      <rect width={round(FIVE_STRIPES)} height={round(FIVE_STRIPES)} fill="#0d5eaf" />
      {cross(FIVE_STRIPES / 2, FIVE_STRIPES / 2, FIVE_STRIPES, FIVE_STRIPES, H / 9, WHITE)}
    </>
  ),
  HR: () => (
    <>
      {horizontal(["#ff0000", WHITE, "#171796"])}
      <path d={shield(12, 5.8, 3.6, 4.4)} fill="#ff0000" />
      <rect x={12} y={5.8} width={1.8} height={1.3} fill={WHITE} />
      <rect x={10.2} y={7.1} width={1.8} height={1.3} fill={WHITE} />
    </>
  ),
  HU: () => horizontal(["#ce2939", WHITE, "#477050"]),
  IL: () => {
    const band = round((15 / 88) * H);
    const up: readonly Point[] = [
      [12, 5.4],
      [14.25, 9.3],
      [9.75, 9.3],
    ];
    const down = up.map(([x, y]): Point => [x, H - y]);
    return (
      <>
        <rect width={W} height={H} fill={WHITE} />
        <rect y={band} width={W} height={band} fill="#0038b8" />
        <rect y={round(H - 2 * band)} width={W} height={band} fill="#0038b8" />
        <polygon points={points(up)} fill="none" stroke="#0038b8" strokeWidth={0.55} />
        <polygon points={points(down)} fill="none" stroke="#0038b8" strokeWidth={0.55} />
      </>
    );
  },
  IT: () => vertical(["#009246", WHITE, "#ce2b37"]),
  LB: () => (
    <>
      {horizontal(["#ed1c24", WHITE, "#ed1c24"], [1, 2, 1])}
      <path
        d="M11.3 4.6h1.4l1.1 1.7h-.8l1.7 2.1h-1.9l2.6 2.3h-2.9v1h-1v-1H8.6l2.6-2.3H9.3L11 6.3h-.8z"
        fill="#00a651"
      />
    </>
  ),
  LU: () => horizontal(["#ed2939", WHITE, "#00a1de"]),
  MD: () => (
    <>
      {vertical(["#0046ae", "#ffd200", "#cc092f"])}
      <path d="M8.8 6.6c1.2-1.9 5.2-1.9 6.4 0c-1.9.8-4.5.8-6.4 0z" fill="#9a6b2c" />
      <circle cx={12} cy={5.4} r={0.55} fill="#9a6b2c" />
      <path d={shield(12, 5.9, 3.2, 4.6)} fill="#0046ae" />
      <rect x={10.4} y={5.9} width={3.2} height={2.3} fill="#cc092f" />
    </>
  ),
  MX: () => (
    <>
      {vertical(["#006847", WHITE, "#ce1126"])}
      <circle cx={12} cy={7.6} r={1.7} fill="#8c5a2b" />
      <path d="M9.7 8.8a2.3 2.3 0 0 0 4.6 0" fill="none" stroke="#006847" strokeWidth={0.6} />
    </>
  ),
  NL: () => horizontal(["#ae1c28", WHITE, "#21468b"]),
  NZ: () => (
    <>
      <rect width={W} height={H} fill="#012169" />
      <g transform="scale(0.5)">{unionJack()}</g>
      {dots(SOUTHERN_CROSS_NZ, 1.35, WHITE)}
      {dots(SOUTHERN_CROSS_NZ, 0.95, "#c8102e")}
    </>
  ),
  PT: () => (
    <>
      {vertical(["#006600", "#ff0000"], [2, 3])}
      <circle cx={9.6} cy={8} r={3.4} fill="none" stroke="#ffe900" strokeWidth={1} />
      <path d={shield(9.6, 5.5, 3.2, 5)} fill="#ff0000" />
      <path d={shield(9.6, 6.3, 1.8, 3.4)} fill={WHITE} />
      <rect x={9.15} y={7.2} width={0.9} height={1.1} fill="#003399" />
    </>
  ),
  RO: () => vertical(["#002b7f", "#fcd116", "#ce1126"]),
  SI: () => (
    <>
      {horizontal([WHITE, "#005da4", "#ed1c24"])}
      <path d={shield(6, 3.2, 3.6, 4.4)} fill="#005da4" stroke="#ed1c24" strokeWidth={0.4} />
      <path d="M4.6 6.6l.6-1.2.4.5.4-1.1.4 1.1.4-.5.6 1.2z" fill={WHITE} />
    </>
  ),
  TR: () => (
    <>
      <rect width={W} height={H} fill="#e30a17" />
      <circle cx={9.5} cy={8} r={4} fill={WHITE} />
      <circle cx={10.5} cy={8} r={3.2} fill="#e30a17" />
      {star(15.1, 8, 1.7, WHITE, Math.PI)}
    </>
  ),
  US: () => (
    <>
      {horizontal(alternating(13, "#b22234", WHITE))}
      <rect width={9.6} height={round((7 * H) / 13)} fill="#3c3b6e" />
      {dots(
        [2.4, 4.8, 7.2].flatMap((x) => [2.15, 4.31, 6.46].map((y): Point => [x, y])),
        0.75,
        WHITE,
      )}
    </>
  ),
  UY: () => (
    <>
      {horizontal(alternating(9, WHITE, "#0038a8"))}
      <rect width={round(FIVE_STRIPES)} height={round(FIVE_STRIPES)} fill={WHITE} />
      {sun(FIVE_STRIPES / 2, FIVE_STRIPES / 2, 1.6, "#fcd116")}
    </>
  ),
  ZA: () => (
    <>
      <rect width={W} height={H / 2} fill="#e03c31" />
      <rect y={H / 2} width={W} height={H / 2} fill="#001489" />
      <polygon points={fork(H / 6)} fill={WHITE} />
      <polygon points={fork(H / 10)} fill="#007a4d" />
      <polygon points={hoistTriangle(H / 10)} fill="#ffb81c" />
      <polygon points={hoistTriangle(H / 10 + H / 15)} fill="#000" />
    </>
  ),
};

/** Every code `flagShapes` knows, uppercase, sorted. */
export const FLAG_CODES: readonly string[] = Object.keys(FLAGS).toSorted();

/** Lookup through a Map rather than the record itself, so a code such as
 * "constructor" reaches no Object.prototype member: only registry keys hit. */
const FLAG_LOOKUP: ReadonlyMap<string, () => React.ReactNode> = new Map(Object.entries(FLAGS));

/** SVG children for a 3:2 flag drawn in a `viewBox="0 0 24 16"` coordinate
 * space; the caller supplies the <svg>. */
export function flagShapes(code: string | undefined): React.ReactNode | undefined {
  return code === undefined ? undefined : FLAG_LOOKUP.get(code.toUpperCase())?.();
}
